import { AlertCircle, ArrowLeft, Calendar, Clock } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { IconName } from '../../../components/IconSelector';
import IconSelector from '../../../components/IconSelector';
import { InlineCardEditor } from '../../../components/InlineCardEditor';
import MultiSelectDropdown from '../../../components/MultiSelectDropdown';
import type {
  CardAction,
  CreateRecurrencePayload,
  RecurrenceConfig,
  User,
} from '../../../service/api';
import api, { toCardActionPayload } from '../../../service/api';

type RecurrenceConfigForm = Omit<
  RecurrenceConfig,
  'interval' | 'durationMinutes'
> & {
  interval: number | null;
  durationMinutes: number | null;
};

type CreateRecurrenceFormData = Omit<
  CreateRecurrencePayload,
  'rewardCard' | 'penaltyCard' | 'config'
> & {
  rewardCard: CardAction[];
  penaltyCard: CardAction[];
  config: RecurrenceConfigForm;
};

export default function CreateTaskPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: recurrenceId } = useParams<{ id: string }>();
  const isEditMode = !!recurrenceId;
  const copyFromData = location.state?.copyFrom;

  const WEEKDAYS = [
    '',
    t('common.weekdays.mon'),
    t('common.weekdays.tue'),
    t('common.weekdays.wed'),
    t('common.weekdays.thu'),
    t('common.weekdays.fri'),
    t('common.weekdays.sat'),
    t('common.weekdays.sun'),
  ];

  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingTask, setIsLoadingTask] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateRecurrenceFormData>({
    title: '',
    description: '',
    assignedTo: [],
    rewardCard: [{ type: 'sticker', amount: 5 }],
    penaltyCard: [{ type: 'sticker', amount: -2 }],
    icon: 'check',
    config: {
      frequency: 'week',
      interval: 1,
      byWeekday: [1],
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      durationMinutes: 60,
      dtStart: DateTime.now(),
    },
  });

  const [recurrenceEnd, setRecurrenceEnd] = useState<
    'never' | 'date' | 'count'
  >('never');
  const [endDate, setEndDate] = useState(
    DateTime.now().plus({ months: 3 }).toISODate()!,
  );
  const [endCount, setEndCount] = useState<number | null>(10);

  useEffect(() => {
    fetchUsers();
    if (isEditMode) {
      fetchTask();
    } else if (copyFromData) {
      // Handle copied data
      const copiedTask = copyFromData;
      const now = DateTime.now();
      setFormData({
        title: `${copiedTask.title} (Copy)`,
        description: copiedTask.description || '',
        assignedTo: copiedTask.assignedTo.map((user: User) => user._id),
        rewardCard:
          typeof copiedTask.rewardCard === 'string'
            ? [{ type: 'sticker', amount: 5 }]
            : copiedTask.rewardCard?.actions || [
                { type: 'sticker', amount: 5 },
              ],
        penaltyCard:
          typeof copiedTask.penaltyCard === 'string'
            ? [{ type: 'sticker', amount: -2 }]
            : copiedTask.penaltyCard?.actions || [
                { type: 'sticker', amount: -2 },
              ],
        icon: copiedTask.icon,
        config: {
          ...copiedTask.config,
          // Set to repeat once starting from current time
          frequency: 'day',
          interval: 1,
          byWeekday: undefined,
          byMonthDay: undefined,
          byWeekNo: undefined,
          bySetPos: undefined,
          until: undefined,
          count: 1, // Repeat only once
          dtStart: now, // Start 1 hour from now
        },
      });

      // Reset recurrence end settings for single occurrence
      setRecurrenceEnd('count');
      setEndCount(1);
    }
    // eslint-disable-next-line
  }, [isEditMode, recurrenceId, copyFromData]);

  const fetchUsers = async () => {
    try {
      const fetchedUsers = await api.usersGetAll();
      setUsers(fetchedUsers);
    } catch {
      setError(t('createTask.failedToLoadUsers'));
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchTask = async () => {
    if (!recurrenceId) return;
    setIsLoadingTask(true);
    try {
      const task = await api.taskGetById(recurrenceId);
      setFormData({
        title: task.title,
        description: task.description || '',
        assignedTo: task.assignedTo.map((user: User) => user._id),
        rewardCard:
          typeof task.rewardCard === 'string'
            ? [{ type: 'sticker', amount: 5 }]
            : task.rewardCard?.actions || [{ type: 'sticker', amount: 5 }],
        penaltyCard:
          typeof task.penaltyCard === 'string'
            ? [{ type: 'sticker', amount: -2 }]
            : task.penaltyCard?.actions || [{ type: 'sticker', amount: -2 }],
        icon: task.icon,
        config: task.config,
      });

      if (task.config.until) {
        setRecurrenceEnd('date');
        setEndDate(task.config.until.toISODate()!);
      } else if (task.config.count) {
        setRecurrenceEnd('count');
        setEndCount(task.config.count);
      } else {
        setRecurrenceEnd('never');
      }
    } catch {
      setError(t('createTask.failedToLoadTask'));
    } finally {
      setIsLoadingTask(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const finalConfig: RecurrenceConfig = {
        ...formData.config,
        interval: formData.config.interval ?? 1,
        durationMinutes: formData.config.durationMinutes ?? 1,
      };

      // Remove _id from config if it exists
      delete finalConfig._id;

      if (recurrenceEnd === 'date') {
        finalConfig.until = DateTime.fromISO(endDate);
        delete finalConfig.count;
      } else if (recurrenceEnd === 'count') {
        finalConfig.count = endCount ?? 1;
        delete finalConfig.until;
      } else {
        delete finalConfig.until;
        delete finalConfig.count;
      }

      // Convert CardAction[] to API format
      const rewardCardForApi = toCardActionPayload(formData.rewardCard);
      const penaltyCardForApi =
        formData.penaltyCard.length > 0
          ? toCardActionPayload(formData.penaltyCard)
          : undefined;

      const payload: CreateRecurrencePayload = {
        title: formData.title,
        description: formData.description || undefined,
        assignedTo: formData.assignedTo,
        rewardCard: rewardCardForApi,
        penaltyCard: penaltyCardForApi,
        icon: formData.icon,
        config: finalConfig,
      };

      if (isEditMode) {
        await api.recurrenceUpdate(recurrenceId!, payload);
      } else {
        await api.taskCreate(payload);
      }
      navigate('/manager/tasks');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : isEditMode
            ? t('createTask.failedToUpdateTask')
            : t('createTask.failedToCreateTask'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!recurrenceId) return;
    if (!window.confirm(t('createTask.confirmDelete'))) {
      return;
    }

    setError(null);
    setIsDeleting(true);
    try {
      await api.recurrenceDelete(recurrenceId);
      navigate('/manager/tasks');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('createTask.failedToDeleteTask'),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const updateConfig = (updates: Partial<RecurrenceConfigForm>) => {
    setFormData({
      ...formData,
      config: { ...formData.config, ...updates },
    });
  };

  const toggleWeekday = (day: number) => {
    const current = formData.config.byWeekday || [];
    const updated = current.includes(day)
      ? current.filter(d => d !== day)
      : [...current, day].sort();

    updateConfig({ byWeekday: updated });
  };

  const getFrequencyLabel = (frequency: string, interval: number): string => {
    if (interval > 1) {
      return t(`createTask.frequencies.${frequency}_other`);
    }
    return t(`createTask.frequencies.${frequency}`);
  };

  const getRecurrenceSummary = () => {
    const { frequency } = formData.config;
    const interval = formData.config.interval ?? 1;
    const frequencyLabel = getFrequencyLabel(frequency, interval);

    let summary: string;
    if (interval > 1) {
      summary = t('createTask.recurrenceEveryInterval', {
        interval,
        frequency: frequencyLabel,
      });
    } else {
      summary = t('createTask.recurrenceEvery', {
        frequency: frequencyLabel,
      });
    }

    if (frequency === 'week' && formData.config.byWeekday?.length) {
      const days = formData.config.byWeekday.map(d => WEEKDAYS[d]).join(', ');
      summary = t('createTask.recurrenceOn', { summary, days });
    }

    return summary;
  };

  const handleIconChange = (icon: IconName) => {
    setFormData({ ...formData, icon: icon as CreateRecurrencePayload['icon'] });
  };

  return (
    <div className="bg-white min-vh-100">
      <Container fluid>
        <Row className="align-items-center bg-white p-4 pb-2">
          <Col xs="auto">
            <Button
              variant="link"
              className="p-0 text-dark"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={24} />
            </Button>
          </Col>
          <Col>
            <h2 className="h5 fw-bold mb-0">
              {isEditMode
                ? t('createTask.editTask')
                : copyFromData
                  ? t('createTask.createTaskCopy')
                  : t('createTask.title')}
            </h2>
          </Col>
        </Row>
        {isLoadingTask ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <Form onSubmit={handleSubmit} className="p-4">
            {error && (
              <Alert variant="danger" className="mb-4">
                <AlertCircle size={16} className="me-2" />
                {error}
              </Alert>
            )}
            <h6 className="fw-bold mb-3">{t('createTask.taskDetails')}</h6>
            <Form.Group className="mb-3">
              <Form.Label>{t('createTask.titleLabel')} *</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={e =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
                placeholder={t('createTask.titlePlaceholder')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('createTask.descriptionLabel')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={e =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder={t('createTask.descriptionPlaceholder')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('createTask.assignToLabel')} *</Form.Label>
              <MultiSelectDropdown
                options={users.map(user => ({
                  value: user._id,
                  label: `${user.given_name} ${user.family_name}`,
                  sublabel: user.email,
                }))}
                value={formData.assignedTo}
                onChange={selectedValues =>
                  setFormData({ ...formData, assignedTo: selectedValues })
                }
                placeholder={t('common.selectUsers')}
                disabled={isLoadingUsers}
              />
            </Form.Group>

            <InlineCardEditor
              label={t('createTask.rewardCardLabel')}
              actions={formData.rewardCard}
              onChange={(actions: CardAction[]) =>
                setFormData({ ...formData, rewardCard: actions })
              }
              required
            />

            <InlineCardEditor
              label={t('createTask.penaltyCardLabel')}
              actions={formData.penaltyCard}
              onChange={(actions: CardAction[]) =>
                setFormData({ ...formData, penaltyCard: actions })
              }
            />

            <Form.Group className="mb-4">
              <Form.Label>{t('createTask.iconLabel')}</Form.Label>
              <IconSelector
                value={formData.icon}
                onChange={handleIconChange}
                className="w-100"
              />
            </Form.Group>
            <h6 className="fw-bold mb-3">{t('createTask.schedule')}</h6>
            <Row>
              <Col xs={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {t('createTask.startDateTimeLabel')} *
                  </Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={formData.config.dtStart.toFormat(
                      "yyyy-MM-dd'T'HH:mm",
                    )}
                    onChange={e =>
                      updateConfig({
                        dtStart: DateTime.fromFormat(
                          e.target.value,
                          "yyyy-MM-dd'T'HH:mm",
                          {
                            zone: formData.config.tz,
                          },
                        ),
                      })
                    }
                    required
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('createTask.durationLabel')} *</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="1"
                      value={formData.config.durationMinutes ?? ''}
                      onChange={e => {
                        const rawValue = e.target.value;
                        const value =
                          rawValue === ''
                            ? null
                            : Math.max(1, parseInt(rawValue, 10) || 1);
                        updateConfig({
                          durationMinutes: value,
                        });
                      }}
                      required
                    />
                    <InputGroup.Text>
                      <Clock size={16} />
                    </InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-4">
              <Form.Label>{t('createTask.recurrenceLabel')}</Form.Label>
              <Button
                variant="outline-primary"
                className="d-flex align-items-center gap-2 w-100"
                onClick={() => setShowRecurrenceModal(true)}
                type="button"
              >
                <Calendar size={20} />
                <span className="flex-grow-1 text-start">
                  {getRecurrenceSummary()}
                </span>
              </Button>
            </Form.Group>
            <div className="d-flex gap-2">
              <Button
                variant="secondary"
                onClick={() => navigate(-1)}
                disabled={isSubmitting || isDeleting}
              >
                {t('common.cancel')}
              </Button>
              {isEditMode && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleDelete}
                  disabled={isSubmitting || isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {t('createTask.deleting')}
                    </>
                  ) : (
                    t('common.delete')
                  )}
                </Button>
              )}
              <Button
                type="submit"
                variant="primary"
                disabled={
                  isSubmitting ||
                  isDeleting ||
                  !formData.title ||
                  formData.assignedTo.length === 0 ||
                  formData.rewardCard.length === 0
                }
                className="flex-grow-1"
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {isEditMode
                      ? t('createTask.updating')
                      : t('createTask.creating')}
                  </>
                ) : isEditMode ? (
                  t('createTask.updateTask')
                ) : (
                  t('createTask.createTask')
                )}
              </Button>
            </div>
          </Form>
        )}
      </Container>
      <Modal
        show={showRecurrenceModal}
        onHide={() => setShowRecurrenceModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('createTask.recurrenceModal.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-4">
            <Form.Label>
              {t('createTask.recurrenceModal.repeatEvery')}
            </Form.Label>
            <Row>
              <Col xs={4}>
                <Form.Control
                  type="number"
                  min="1"
                  value={formData.config.interval ?? ''}
                  onChange={e => {
                    const rawValue = e.target.value;
                    const value =
                      rawValue === ''
                        ? null
                        : Math.max(1, parseInt(rawValue, 10) || 1);
                    updateConfig({ interval: value });
                  }}
                />
              </Col>
              <Col xs={8}>
                <Form.Select
                  value={formData.config.frequency}
                  onChange={e =>
                    updateConfig({
                      frequency: e.target
                        .value as RecurrenceConfig['frequency'],
                    })
                  }
                >
                  <option value="day">
                    {getFrequencyLabel('day', formData.config.interval ?? 1)}
                  </option>
                  <option value="week">
                    {getFrequencyLabel('week', formData.config.interval ?? 1)}
                  </option>
                  <option value="month">
                    {getFrequencyLabel('month', formData.config.interval ?? 1)}
                  </option>
                  <option value="year">
                    {getFrequencyLabel('year', formData.config.interval ?? 1)}
                  </option>
                </Form.Select>
              </Col>
            </Row>
          </Form.Group>
          {formData.config.frequency === 'week' && (
            <Form.Group className="mb-4">
              <Form.Label>
                {t('createTask.recurrenceModal.repeatOn')}
              </Form.Label>
              <ButtonGroup className="d-flex">
                {WEEKDAYS.map((day, index) =>
                  index === 0 ? null : (
                    <Button
                      key={day}
                      variant={
                        formData.config.byWeekday?.includes(index)
                          ? 'primary'
                          : 'outline-secondary'
                      }
                      onClick={() => toggleWeekday(index)}
                      type="button"
                    >
                      {day[0]}
                    </Button>
                  ),
                )}
              </ButtonGroup>
            </Form.Group>
          )}
          <Form.Group>
            <Form.Label>{t('createTask.recurrenceModal.ends')}</Form.Label>
            <div className="mb-3">
              <Form.Check
                type="radio"
                id="never"
                label={t('createTask.recurrenceModal.never')}
                checked={recurrenceEnd === 'never'}
                onChange={() => setRecurrenceEnd('never')}
              />
              <Form.Check
                type="radio"
                id="on-date"
                label={t('createTask.recurrenceModal.on')}
                checked={recurrenceEnd === 'date'}
                onChange={() => setRecurrenceEnd('date')}
              />
              {recurrenceEnd === 'date' && (
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="ms-4 mt-2"
                  style={{ maxWidth: '200px' }}
                />
              )}
              <Form.Check
                type="radio"
                id="after-count"
                label={t('createTask.recurrenceModal.after')}
                checked={recurrenceEnd === 'count'}
                onChange={() => setRecurrenceEnd('count')}
              />
              {recurrenceEnd === 'count' && (
                <InputGroup className="ms-4 mt-2" style={{ maxWidth: '200px' }}>
                  <Form.Control
                    type="number"
                    min="1"
                    value={endCount ?? ''}
                    onChange={e => {
                      const rawValue = e.target.value;
                      const value =
                        rawValue === ''
                          ? null
                          : Math.max(1, parseInt(rawValue, 10) || 1);
                      setEndCount(value);
                    }}
                  />
                  <InputGroup.Text>
                    {t('createTask.recurrenceModal.occurrences')}
                  </InputGroup.Text>
                </InputGroup>
              )}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowRecurrenceModal(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowRecurrenceModal(false)}
          >
            {t('common.done')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
